import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { Building2, Clock, Contact, FileBadge, Globe2, Languages, Users } from 'lucide-react'
import { useId } from 'react'
import {
  Controller,
  useForm,
  useWatch,
  type Control,
  type FieldErrors,
  type UseFormRegister,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useGuardians } from '@/features/auth/guardians'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormActions, FormSection } from '@/components/forms/FormSection'
import { FormField } from '@/components/forms/FormField'
import { ImageUpload } from '@/components/forms/ImageUpload'
import { ErrorState, LoadingState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { Switch } from '@/components/ui/Switch'
import { Textarea } from '@/components/ui/Textarea'
import { useAuth } from '@/features/auth/useAuth'
import { ToggleRow } from '@/features/lms/components/ToggleRow'
import { schoolQuery, useUpdateSchool } from '@/features/school/api'
import { SCHOOL_TYPES, TYPE_VOCABULARY, WORDS } from '@/features/school/vocabulary'
import { applyVocabulary, type Vocabulary } from '@/i18n/vocabulary'
import {
  ACADEMIC_YEAR_PATTERNS,
  schoolSchema,
  toSchoolFormValues,
  toSchoolPayload,
  WEEKDAYS,
  type SchoolFormInput,
  type SchoolFormOutput,
} from '@/features/school/schemas'
import type { SchoolSettings } from '@/features/school/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { intlLocale, tAny } from '@/i18n'
import { cn } from '@/utils/cn'
import { formatDateTime } from '@/utils/format'

const FIELDS = [
  'logoFileId',
  'name',
  'shortName',
  'motto',
  'establishedYear',
  'institutionCodeLabel',
  'institutionCode',
  'email',
  'phone',
  'website',
  'address',
  'countryCode',
  'currency',
  'timezone',
  'locale',
  'academicYearPattern',
  'openingHours',
] as const

const TIME_ZONES: readonly string[] = 'supportedValuesOf' in Intl ? Intl.supportedValuesOf('timeZone') : []

export function SchoolSettingsPage() {
  useDocumentTitle('School settings')
  const { t } = useTranslation('settings')
  const school = useQuery(schoolQuery())

  return (
    <>
      <PageHeader
        title={t('School settings')}
        description={t('Identity, contact details and regional preferences used across every portal.')}
        breadcrumbs={[{ label: t('Settings') }, { label: t('School') }]}
        homeHref="/admin"
        meta={
          school.data ? (
            <span className="text-xs text-muted">
              {t('Last updated {{date}}', { date: formatDateTime(school.data.updatedAt) })}
            </span>
          ) : null
        }
      />
      {school.isPending ? (
        <LoadingState />
      ) : school.error ? (
        <ErrorState onRetry={() => void school.refetch()} />
      ) : (
        <SchoolForm school={school.data} />
      )}
    </>
  )
}

function SchoolForm({ school }: { school: SchoolSettings }) {
  const { t } = useTranslation('settings')
  const update = useUpdateSchool()
  const { school: session, setSchool } = useAuth()
  const withGuardians = useGuardians()
  const tzListId = useId()
  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setError,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<SchoolFormInput, unknown, SchoolFormOutput>({
    resolver: zodResolver(schoolSchema),
    defaultValues: toSchoolFormValues(school),
  })
  const name = watch('name')

  const submit = handleSubmit(async (values) => {
    try {
      const saved = await update.mutateAsync(toSchoolPayload(values))
      reset(toSchoolFormValues(saved))
      // Menus, forms and the portal name follow the guardians setting right away.
      if (session)
        setSchool({
          ...session,
          guardiansEnabled: saved.guardiansEnabled,
          type: saved.type,
          vocabulary: saved.vocabulary,
        })
      toast.success(t('Settings saved'), t('School details were updated.'))
    } catch (e) {
      applyServerErrors(e, setError, FIELDS)
    }
  })

  return (
    <form onSubmit={(e) => void submit(e)} noValidate className="space-y-6">
      <FormSection
        title={t('Identity')}
        description={t('Shown on the sign-in screen, portals and printed documents.')}
        icon={<Building2 />}
      >
        <div className="sm:col-span-2">
          <Controller
            control={control}
            name="logoFileId"
            render={({ field }) => (
              <ImageUpload
                name={name}
                label={t('Logo')}
                category="logo"
                shape="square"
                currentUrl={school.logoUrl}
                onUploaded={(id) => field.onChange(id)}
              />
            )}
          />
        </div>
        <FormField label={t('School name')} error={errors.name?.message} required className="sm:col-span-2">
          <Input autoComplete="organization" {...register('name')} />
        </FormField>
        <FormField
          label={t('Short name')}
          error={errors.shortName?.message}
          hint={t('Used where space is tight, e.g. the sidebar.')}
        >
          <Input {...register('shortName')} />
        </FormField>
        <FormField label={t('Established')} error={errors.establishedYear?.message}>
          <Input
            type="number"
            inputMode="numeric"
            placeholder="2004"
            min={1800}
            max={new Date().getFullYear()}
            {...register('establishedYear')}
          />
        </FormField>
        <FormField label={t('Motto')} error={errors.motto?.message} className="sm:col-span-2">
          <Input {...register('motto')} />
        </FormField>
      </FormSection>

      <FormSection
        title={t('Registration')}
        description={t('The official identifier issued by your education authority.')}
        icon={<FileBadge />}
      >
        <FormField
          label={t('Identifier label')}
          error={errors.institutionCodeLabel?.message}
          required
          hint={t('e.g. Registration No., EMIS code, URN.')}
        >
          <Input {...register('institutionCodeLabel')} />
        </FormField>
        <FormField label={t('Identifier')} error={errors.institutionCode?.message}>
          <Input className="font-mono" {...register('institutionCode')} />
        </FormField>
      </FormSection>

      <FormSection title={t('Contact')} icon={<Contact />}>
        <FormField label={t('Email')} error={errors.email?.message}>
          <Input type="email" autoComplete="email" {...register('email')} />
        </FormField>
        <FormField label={t('Phone')} error={errors.phone?.message}>
          <Input type="tel" autoComplete="tel" {...register('phone')} />
        </FormField>
        <FormField label={t('Website')} error={errors.website?.message} className="sm:col-span-2">
          <Input type="url" placeholder="https://" {...register('website')} />
        </FormField>
        <FormField label={t('Address')} error={errors.address?.message} className="sm:col-span-2">
          <Textarea rows={3} autoComplete="street-address" {...register('address')} />
        </FormField>
      </FormSection>

      <FormSection
        title={t('Locale')}
        description={t('Formats dates, money and the academic calendar.')}
        icon={<Globe2 />}
        columns={3}
      >
        <FormField
          label={t('Country code')}
          error={errors.countryCode?.message}
          hint={t('ISO 3166, 2 letters.')}
        >
          <Input maxLength={2} className="uppercase" placeholder="US" {...register('countryCode')} />
        </FormField>
        <FormField
          label={t('Currency')}
          error={errors.currency?.message}
          required
          hint={t('ISO 4217, 3 letters.')}
        >
          <Input maxLength={3} className="uppercase" placeholder="USD" {...register('currency')} />
        </FormField>
        <FormField
          label={t('Locale')}
          error={errors.locale?.message}
          required
          hint={t('e.g. en, fr or en-GB.')}
        >
          <Input {...register('locale')} />
        </FormField>
        <FormField
          label={t('Time zone')}
          error={errors.timezone?.message}
          required
          className="sm:col-span-2 lg:col-span-1"
        >
          <Input
            list={TIME_ZONES.length ? tzListId : undefined}
            placeholder="Africa/Nairobi"
            autoComplete="off"
            {...register('timezone')}
          />
        </FormField>
        {TIME_ZONES.length ? (
          <datalist id={tzListId}>
            {TIME_ZONES.map((tz) => (
              <option key={tz} value={tz} />
            ))}
          </datalist>
        ) : null}
        <FormField
          label={t('Academic year runs')}
          error={errors.academicYearPattern?.message}
          required
          className="sm:col-span-2"
        >
          <Select
            options={ACADEMIC_YEAR_PATTERNS.map((p) => ({ value: p.value, label: t(p.label) }))}
            {...register('academicYearPattern')}
          />
        </FormField>
      </FormSection>

      <FormSection
        title={t('Type and vocabulary')}
        description={t(
          'What kind of institution this is, and the words used everywhere for learners, the year and teaching groups.',
        )}
        icon={<Languages />}
        columns={1}
      >
        <VocabularyFields control={control} />
      </FormSection>

      <FormSection
        title={t('Families')}
        description={t('Whether parents and guardians take part in school life through the platform.')}
        icon={<Users />}
        columns={1}
      >
        <Controller
          control={control}
          name="guardiansEnabled"
          render={({ field }) => (
            <ToggleRow
              label={t('Guardian accounts')}
              hint={
                field.value
                  ? t(
                      'Guardians are recorded on each student, can have a portal account and receive notices and messages.',
                    )
                  : t(
                      'Students only: no guardian records, parent accounts or notices to parents. The family portal becomes the student portal. Existing guardian data is kept.',
                    )
              }
              checked={field.value}
              onCheckedChange={field.onChange}
            />
          )}
        />
      </FormSection>

      <FormSection
        title={t('Opening hours')}
        description={
          withGuardians ? t('Shown to families in their portal.') : t('Shown to students in their portal.')
        }
        icon={<Clock />}
        columns={1}
      >
        <OpeningHours control={control} register={register} errors={errors} />
      </FormSection>

      <FormActions>
        <Button variant="secondary" onClick={() => reset()} disabled={!isDirty || isSubmitting}>
          {t('Discard changes')}
        </Button>
        <Button type="submit" loading={isSubmitting} disabled={!isDirty}>
          {t('Save settings')}
        </Button>
      </FormActions>
    </form>
  )
}

const TERMS = [
  { term: 'learner', label: 'Learners are called' },
  { term: 'year', label: 'The year is called' },
  { term: 'class', label: 'Teaching levels are called' },
  { term: 'section', label: 'Groups within a level are called' },
] as const

/** School type, and the four words (each can follow the type or be chosen), with a live example. */
function VocabularyFields({ control }: { control: Control<SchoolFormInput, unknown, SchoolFormOutput> }) {
  const { t, i18n } = useTranslation('settings')
  const lang = i18n.language === 'en' ? 'en' : 'fr'
  const type = useWatch({ control, name: 'type' })
  const chosen = useWatch({ control, name: 'vocabulary' })
  const defaults = TYPE_VOCABULARY[type]
  const effective: Vocabulary = {
    learner: (chosen.learner || defaults.learner) as Vocabulary['learner'],
    year: (chosen.year || defaults.year) as Vocabulary['year'],
    class: (chosen.class || defaults.class) as Vocabulary['class'],
    section: (chosen.section || defaults.section) as Vocabulary['section'],
  }
  const example =
    lang === 'en'
      ? "Choose the student's academic year, class and section."
      : 'Choisissez l’année scolaire, la classe et la section de l’élève.'
  const typeMeta = SCHOOL_TYPES.find((s) => s.value === type)

  return (
    <div className="space-y-4">
      <FormField label={t('Type of institution')} hint={typeMeta ? tAny(typeMeta.description) : undefined}>
        <Controller
          control={control}
          name="type"
          render={({ field }) => (
            <Select
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              options={SCHOOL_TYPES.map((s) => ({ value: s.value, label: tAny(s.label) }))}
            />
          )}
        />
      </FormField>
      <div className="grid gap-4 sm:grid-cols-2">
        {TERMS.map(({ term, label }) => {
          const words = WORDS[term] as Record<string, { fr: string; en: string }>
          return (
            <FormField key={term} label={t(label)}>
              <Controller
                control={control}
                name={`vocabulary.${term}`}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    options={[
                      {
                        value: '',
                        label: t('As for this type ({{word}})', {
                          word: words[defaults[term]]?.[lang] ?? '',
                        }),
                      },
                      ...Object.entries(words).map(([value, w]) => ({ value, label: w[lang] })),
                    ]}
                  />
                )}
              />
            </FormField>
          )
        })}
      </div>
      <p className="rounded-control bg-surface-muted px-3 py-2 text-sm text-ink-soft">
        <span className="font-medium text-ink">{t('Example:')}</span>{' '}
        {applyVocabulary(example, lang, effective)}
      </p>
    </div>
  )
}

interface OpeningHoursProps {
  control: Control<SchoolFormInput, unknown, SchoolFormOutput>
  register: UseFormRegister<SchoolFormInput>
  errors: FieldErrors<SchoolFormInput>
}

/** Localised weekday name; index 0 is Sunday (4 Jan 2026 was a Sunday). */
function weekdayName(index: number): string {
  const name = new Intl.DateTimeFormat(intlLocale(), { weekday: 'long', timeZone: 'UTC' }).format(
    new Date(Date.UTC(2026, 0, 4 + index)),
  )
  return name.charAt(0).toUpperCase() + name.slice(1)
}

function OpeningHours({ control, register, errors }: OpeningHoursProps) {
  const { t } = useTranslation('settings')
  return (
    <ul className="-my-1 divide-y divide-line">
      {WEEKDAYS.map((key, i) => {
        const day = weekdayName(i)
        return (
          <li key={key} className="py-2.5">
            <Controller
              control={control}
              name={`openingHours.${i}.isOpen`}
              render={({ field }) => {
                const rowErrors = errors.openingHours?.[i]
                const closed = !field.value
                return (
                  <div className="grid grid-cols-[7rem_minmax(0,1fr)] items-center gap-x-4 gap-y-2 sm:grid-cols-[9rem_6rem_minmax(0,1fr)]">
                    <span className="font-medium text-ink">{day}</span>
                    <label className="flex items-center gap-2 text-xs text-ink-soft">
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        aria-label={t('Open on {{day}}', { day })}
                      />
                      <span className={cn(closed && 'text-muted')}>{closed ? t('Closed') : t('Open')}</span>
                    </label>
                    <div className="col-span-2 flex flex-wrap items-start gap-2 sm:col-span-1">
                      <FormField
                        label={t('{{day}} opening time', { day })}
                        hideLabel
                        error={rowErrors?.openTime?.message}
                        className="w-32"
                      >
                        <Input
                          type="time"
                          size="sm"
                          disabled={closed}
                          {...register(`openingHours.${i}.openTime`)}
                        />
                      </FormField>
                      <span className="pt-1.5 text-muted" aria-hidden="true">
                        –
                      </span>
                      <FormField
                        label={t('{{day}} closing time', { day })}
                        hideLabel
                        error={rowErrors?.closeTime?.message}
                        className="w-32"
                      >
                        <Input
                          type="time"
                          size="sm"
                          disabled={closed}
                          {...register(`openingHours.${i}.closeTime`)}
                        />
                      </FormField>
                    </div>
                  </div>
                )
              }}
            />
          </li>
        )
      })}
    </ul>
  )
}
